import type { EventContext } from "@src/types";
import { PROD_DOMAINS } from "./userInfo";

type AuthActionType = 'login' | 'sign_up'

const TIMEOUT_MS = 5000

// a rota identifica o usuário pelo cookie de login, que o browser só envia para o host onde o login
// aconteceu (secure.<domínio>); por isso usa sempre o domínio de checkout, mesmo quando chamada da loja
const getAuthActionDomain = (account?: string) => {
  if (account && PROD_DOMAINS[account]) return PROD_DOMAINS[account].checkout
  if (typeof window === 'undefined' || !window.location?.origin) return null

  const { origin } = window.location
  if (/^https:\/\/(www|secure)\./.test(origin)) return origin.replace('://www.', '://secure.')
  if (/\.myvtex\.com$/.test(origin)) return origin

  return null
}

/**
 * `type` do auth_action: quem decide é o Master Data (tem CPF cadastrado na CL => login; não tem => sign_up).
 * `meta.user_info` é opcional: `account` escolhe o domínio e `userEmail` é o fallback da rota para
 * onde o cookie de login não chega (preview, localhost). Se a consulta falhar, vale o `fallback`.
 */
export async function getAuthActionType(context: EventContext, fallback: unknown = null) {
  const userInfo = context?.meta?.user_info
  const domain = getAuthActionDomain(userInfo?.account)

  if (!domain) return fallback

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timeout = setTimeout(() => controller?.abort(), TIMEOUT_MS)

  try {
    const response = await fetch(`${domain}/_v/auth-action`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(userInfo?.userEmail ? { email: userInfo.userEmail } : {}),
      signal: controller?.signal,
    })

    if (response.status !== 200) return fallback

    const res: { type?: AuthActionType } = await response.json()

    return res?.type === 'login' || res?.type === 'sign_up' ? res.type : fallback
  } catch (_) {
    return fallback
  } finally {
    clearTimeout(timeout)
  }
}
